import { useState, useEffect } from 'react';
import i18n from '../i18n';
import { translateText } from '../services/translate';
import '../styles/AdminDashboard.css';
import '../styles/Partners.css';
import '../styles/AdminActivites.css';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/activites/`;

const emptyForm = {
  titre: '', titre_en: '', titre_ar: '',
  description: '', description_en: '', description_ar: '',
  categorie: '', categorie_en: '', categorie_ar: '',
  is_active: true
};

const IconTrash = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const IconEdit = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const IconPhoto = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

export function AdminActivitesContent() {

  const [activites, setActivites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [photoModal, setPhotoModal] = useState(null);
  const [newPhoto, setNewPhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [modal, setModal] = useState({ show: false, title: '', message: '', type: 'info', onConfirm: null });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [openCatDropdown, setOpenCatDropdown] = useState(false);

  const filteredActivites = activites.filter(a => {
    const matchesCategory = activeFilter === 'ALL' || (a.categorie && a.categorie.toLowerCase() === activeFilter.toLowerCase());
    const matchesSearch = !searchQuery ||
      (a.titre && a.titre.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (a.description && a.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const uniqueCats = [...new Set(activites.map(a => a.categorie ? a.categorie.trim().charAt(0).toUpperCase() + a.categorie.trim().slice(1).toLowerCase() : ''))].filter(Boolean);
  const dynamicCategories = ['ALL', ...uniqueCats];

  const isSuperAdmin = localStorage.getItem('is_superadmin') === 'true';
  const canManage = isSuperAdmin || localStorage.getItem('can_manage_events') === 'true';

  const token = () => localStorage.getItem('access_token');
  const authHeader = () => ({ Authorization: `Bearer ${token()}` });

  const closeModal = () => setModal(m => ({ ...m, show: false, onConfirm: null }));
  const notify = (type, title, message) => setModal({ show: true, type, title, message, onConfirm: null });
  const confirmModal = (title, message, onConfirm) =>
    setModal({ show: true, type: 'warning', title, message, onConfirm });

  const fetchActivites = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_URL, {
        headers: {
          ...authHeader(),
          'Accept-Language': i18n.language || 'fr'
        }
      });
      if (res.ok) setActivites(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchActivites(); }, []); // eslint-disable-line

  // Fermer le dropdown en cliquant en dehors
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.custom-dropdown-container')) {
        setOpenCatDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setPendingPhotos([]); setShowForm(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      titre: a.titre || '', titre_en: a.titre_en || '', titre_ar: a.titre_ar || '',
      description: a.description || '', description_en: a.description_en || '', description_ar: a.description_ar || '',
      categorie: a.categorie || '', categorie_en: a.categorie_en || '', categorie_ar: a.categorie_ar || '',
      is_active: a.is_active
    });
    setPendingPhotos([]);
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditing(null); setForm(emptyForm); setPendingPhotos([]); };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleAutoTranslate = async () => {
    if (!form.titre && !form.description && !form.categorie) {
      notify('warning', 'Champs vides', 'Veuillez remplir au moins le titre, la description ou la catégorie en français.');
      return;
    }

    setSubmitting(true);
    try {
      const translations = {};

      if (form.titre) {
        translations.titre_en = await translateText(form.titre, 'fr', 'en');
        translations.titre_ar = await translateText(form.titre, 'fr', 'ar');
      }

      if (form.description) {
        translations.description_en = await translateText(form.description, 'fr', 'en');
        translations.description_ar = await translateText(form.description, 'fr', 'ar');
      }

      if (form.categorie) {
        translations.categorie_en = await translateText(form.categorie, 'fr', 'en');
        translations.categorie_ar = await translateText(form.categorie, 'fr', 'ar');
      }

      setForm(prev => ({ ...prev, ...translations }));
      notify('success', 'Traduction terminée', 'Les champs ont été traduits automatiquement.');
    } catch {
      notify('error', 'Erreur', 'La traduction a échoué.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const url = editing ? `${API_URL}${editing.id}/` : API_URL;
      const method = editing ? 'PATCH' : 'POST';
      const fd = new FormData();
      fd.append('titre', form.titre);
      fd.append('titre_en', form.titre_en);
      fd.append('titre_ar', form.titre_ar);
      fd.append('description', form.description);
      fd.append('description_en', form.description_en);
      fd.append('description_ar', form.description_ar);
      fd.append('categorie', form.categorie);
      fd.append('categorie_en', form.categorie_en);
      fd.append('categorie_ar', form.categorie_ar);
      fd.append('is_active', form.is_active);
      const res = await fetch(url, { method, headers: authHeader(), body: fd });
      if (res.ok) {
        const created = await res.json();
        if (pendingPhotos.length > 0) {
          await Promise.all(pendingPhotos.map(photo => {
            const photoFd = new FormData();
            photoFd.append('image', photo);
            return fetch(`${API_URL}${created.id}/photos/`, { method: 'POST', headers: authHeader(), body: photoFd });
          }));
        }
        closeForm();
        fetchActivites();
        notify('success', editing ? 'Activité modifiée' : 'Activité ajoutée',
          editing ? 'Modifications enregistrées.' : `Activité créée avec ${pendingPhotos.length} photo(s).`);
      } else {
        const err = await res.json();
        notify('error', 'Erreur', JSON.stringify(err));
      }
    } catch { notify('error', 'Erreur serveur', 'Connexion impossible.'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = (id) => {
    confirmModal('Supprimer l\'activité', 'Cette activité et toutes ses photos seront supprimées. Confirmer ?', async () => {
      const res = await fetch(`${API_URL}${id}/`, { method: 'DELETE', headers: authHeader() });
      if (res.ok) { fetchActivites(); notify('success', 'Supprimée', 'L\'activité a été supprimée.'); }
      else notify('error', 'Erreur', 'Impossible de supprimer.');
    });
  };

  const openPhotoModal = (a) => { setPhotoModal(a); setNewPhoto(null); };
  const closePhotoModal = () => { setPhotoModal(null); setNewPhoto(null); fetchActivites(); };

  const handleFilesChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setPendingPhotos(prev => [...prev, ...newFiles]);
    }
    // Réinitialiser pour permettre de resélectionner le même fichier si besoin
    e.target.value = null;
  };

  const handleUploadPhoto = async () => {
    if (!newPhoto || !photoModal) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('image', newPhoto);
      const res = await fetch(`${API_URL}${photoModal.id}/photos/`, {
        method: 'POST',
        headers: authHeader(),
        body: fd,
      });
      if (res.ok) {
        const updated = await res.json();
        setPhotoModal(prev => ({ ...prev, photos: [...(prev.photos || []), updated] }));
        setNewPhoto(null);
      } else {
        const errText = await res.text();
        notify('error', 'Erreur', `Status ${res.status}: ${errText}`);
      }
    } catch { notify('error', 'Erreur serveur', 'Connexion impossible.'); }
    finally { setUploadingPhoto(false); }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      const res = await fetch(`${API_URL}${photoModal.id}/photos/${photoId}/`, {
        method: 'DELETE', headers: authHeader(),
      });
      if (res.ok) {
        setPhotoModal(prev => ({ ...prev, photos: prev.photos.filter(p => p.id !== photoId) }));
      }
    } catch { console.error('Erreur suppression photo'); }
  };

  return (
    <>
      {/* ── Modal générique ── */}
      {modal.show && (
        <div className="tiwizi-modal-overlay" onClick={closeModal}>
          <div className="tiwizi-modal-card tiwizi-details-modal" onClick={e => e.stopPropagation()}>
            <div className={`tiwizi-modal-icon ${modal.type === 'success' ? 'success' : 'warning'}`}>
              {modal.type === 'success'
                ? <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                : <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
              }
            </div>
            <h3>{modal.title}</h3>
            <p style={{ whiteSpace: 'pre-wrap' }}>{modal.message}</p>
            <div className="tiwizi-modal-actions">
              {modal.onConfirm ? (
                <>
                  <button className="tiwizi-btn-cancel" onClick={closeModal}>Annuler</button>
                  <button className="tiwizi-btn-confirm" onClick={() => { modal.onConfirm(); closeModal(); }}>Confirmer</button>
                </>
              ) : (
                <button className="tiwizi-btn-cancel" onClick={closeModal}>Fermer</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal gestion photos ── */}
      {photoModal && (
        <div className="tiwizi-modal-overlay" onClick={closePhotoModal}>
          <div className="tiwizi-modal-card" style={{ maxWidth: '680px', textAlign: 'left' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: '6px' }}>Photos — {photoModal.titre}</h3>
            <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '20px' }}>
              {photoModal.photos?.length || 0} photo(s)
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '12px', marginBottom: '24px' }}>
              {(photoModal.photos || []).map(photo => (
                <div key={photo.id} style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', border: '1px solid #eee' }}>
                  <img src={photo.image} alt="" style={{ width: '100%', height: '100px', objectFit: 'cover', display: 'block' }} />
                  <button onClick={() => handleDeletePhoto(photo.id)}
                    style={{ position: 'absolute', top: '6px', right: '6px', background: 'rgba(231,76,60,0.85)', color: 'white', border: 'none', borderRadius: '6px', width: '26px', height: '26px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))}
              {(photoModal.photos || []).length === 0 && (
                <p style={{ color: '#aaa', fontStyle: 'italic', gridColumn: '1/-1' }}>Aucune photo pour le moment.</p>
              )}
            </div>
            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '20px' }}>
              <p style={{ fontWeight: '700', marginBottom: '12px', fontSize: '0.9rem' }}>Ajouter une photo</p>
              <label className={`upload-zone ${newPhoto ? 'has-file' : ''}`} style={{ minHeight: '100px' }}>
                {newPhoto ? (
                  <>
                    <img src={URL.createObjectURL(newPhoto)} alt="Prévisu" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }} />
                    <span style={{ fontSize: '0.8rem' }}>{newPhoto.name}</span>
                  </>
                ) : (
                  <>
                    <IconPhoto />
                    <span>Cliquer pour choisir une image</span>
                  </>
                )}
                <input type="file" accept="image/*" onChange={e => setNewPhoto(e.target.files[0] || null)} />
              </label>
              <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                <button onClick={closePhotoModal} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #ddd', background: '#fff', fontWeight: '700', cursor: 'pointer' }}>
                  Fermer
                </button>
                <button onClick={handleUploadPhoto} disabled={!newPhoto || uploadingPhoto} className="btn-submit-partner" style={{ flex: 2 }}>
                  {uploadingPhoto ? 'Upload...' : 'Ajouter la photo'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Barre de recherche & Filtres ── */}
      <div className="admin-search-filter-bar">
        <div className="admin-search-wrapper">
          <svg className="admin-search-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="admin-search-input"
            placeholder="Rechercher par titre ou description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="admin-search-clear" type="button" onClick={() => setSearchQuery('')} title="Effacer">✕</button>
          )}
        </div>
        <div className="admin-filters-wrapper" style={{ display: 'flex', gap: '10px' }}>
          {/* ── Dropdown Catégorie ── */}
          <div className="custom-dropdown-container">
            <button
              type="button"
              className={`custom-dropdown-button ${openCatDropdown ? 'is-open' : ''}`}
              onClick={() => setOpenCatDropdown(o => !o)}
            >
              {activeFilter === 'ALL' ? 'Toutes les catégories' : activeFilter}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points={openCatDropdown ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
              </svg>
            </button>
            {openCatDropdown && (
              <div className="custom-dropdown-menu">
                {dynamicCategories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    className={`custom-dropdown-item ${activeFilter === cat ? 'selected' : ''}`}
                    onClick={() => { setActiveFilter(cat); setOpenCatDropdown(false); }}
                  >
                    {cat === 'ALL' ? 'Toutes les catégories' : cat}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Indicateur de résultats ── */}
      {(searchQuery || activeFilter !== 'ALL') && (
        <div className="admin-results-indicator">
          <span className="results-count">
            {filteredActivites.length} {filteredActivites.length > 1 ? 'activités trouvées' : 'activité trouvée'}
          </span>
        </div>
      )}

      {/* ── Section principale ── */}
      <section className="data-section">
        <div className="section-title-group" style={{ marginBottom: '25px' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>
            Gestion des Activités
            <span className="count-badge dark">{activites.length}</span>
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="projets-view-toggle">
              <button type="button" className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Vue Liste">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
                  <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
              </button>
              <button type="button" className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')} title="Vue Grille">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
                </svg>
              </button>
            </div>
            {canManage && (
              <button onClick={openCreate} className="btn-add-partner">+ Ajouter une activité</button>
            )}
          </div>
        </div>

        {/* ── Formulaire ── */}
        {showForm && (
          <div className="add-partner-form-container">
            <h4 style={{ marginTop: 0, marginBottom: '20px' }}>
              {editing ? 'Modifier l\'activité' : 'Nouvelle activité'}
            </h4>
            <form onSubmit={handleSubmit}>
              <div className="act-admin-form-grid">
                <div className="form-column">
                  <div className="form-field">
                    <label>Titre (FR) <span style={{ color: '#e74c3c' }}>*</span></label>
                    <input type="text" name="titre" value={form.titre} onChange={handleChange} placeholder="Ex: Randonnée..." required />
                  </div>
                  <div className="form-field">
                    <label>Titre (EN)</label>
                    <input type="text" name="titre_en" value={form.titre_en} onChange={handleChange} placeholder="Ex: Hiking..." />
                  </div>
                  <div className="form-field">
                    <label>Titre (AR)</label>
                    <input type="text" name="titre_ar" value={form.titre_ar} onChange={handleChange} placeholder="..." style={{ direction: 'rtl' }} />
                  </div>
                  <div className="form-field">
                    <label>Description (FR) <span style={{ color: '#e74c3c' }}>*</span></label>
                    <textarea name="description" value={form.description} onChange={handleChange} placeholder="Détails..." rows={3} required />
                  </div>
                  <div className="form-field">
                    <label>Description (EN)</label>
                    <textarea name="description_en" value={form.description_en} onChange={handleChange} placeholder="..." rows={3} />
                  </div>
                  <div className="form-field">
                    <label>Description (AR)</label>
                    <textarea name="description_ar" value={form.description_ar} onChange={handleChange} placeholder="..." rows={3} style={{ direction: 'rtl' }} />
                  </div>
                </div>
                <div className="form-column">
                  <div className="form-field">
                    <label>Catégorie (FR) <span style={{ color: '#e74c3c' }}>*</span></label>
                    <input type="text" name="categorie" value={form.categorie} onChange={handleChange} placeholder="Ex: Nature" required style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid #ddd', fontSize: '0.95rem', background: '#fafafa' }} />
                  </div>
                  <div className="form-field">
                    <label>Catégorie (EN)</label>
                    <input type="text" name="categorie_en" value={form.categorie_en} onChange={handleChange} placeholder="Ex: Nature" style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid #ddd', fontSize: '0.95rem', background: '#fafafa' }} />
                  </div>
                  <div className="form-field">
                    <label>Catégorie (AR)</label>
                    <input type="text" name="categorie_ar" value={form.categorie_ar} onChange={handleChange} placeholder="..." style={{ direction: 'rtl', padding: '10px 14px', borderRadius: '10px', border: '1px solid #ddd', fontSize: '0.95rem', background: '#fafafa' }} />
                  </div>
                  <div className="act-admin-checkbox-row">
                    <input type="checkbox" name="is_active" id="is_active" checked={form.is_active} onChange={handleChange} />
                    <label htmlFor="is_active">Afficher sur le site public</label>
                  </div>
                </div>
              </div>

              {/* Zone photos */}
              <div className="act-admin-photos-section">
                <p className="act-admin-photos-title">
                  Photos de l'activité
                </p>
                <label className="act-admin-photo-upload-label">
                  <IconPhoto />
                  Cliquer pour sélectionner des photos
                  <input type="file" accept="image/*" multiple style={{ display: 'none' }}
                    onChange={handleFilesChange} />
                </label>
                {pendingPhotos.length > 0 && (
                  <>
                    <p className="act-admin-photos-count"> {pendingPhotos.length} photo(s) sélectionnée(s)</p>
                    <div className="act-admin-photos-preview">
                      {pendingPhotos.map((photo, idx) => (
                        <div key={idx} className="act-admin-photo-thumb">
                          <img src={URL.createObjectURL(photo)} alt={`Aperçu ${idx + 1}`} />
                          <div className="act-admin-photo-thumb-name">{photo.name}</div>
                          <button type="button" className="act-admin-photo-remove"
                            onClick={() => setPendingPhotos(prev => prev.filter((_, i) => i !== idx))}>✕</button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #eee' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#666' }}>Outils de traduction</span>
                  <button
                    type="button"
                    onClick={handleAutoTranslate}
                    className="btn-translate-auto"
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: '#f5c50a',
                      color: '#1a1a1a', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '0.85rem'
                    }}
                    disabled={submitting}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="16 3 21 3 21 8" /><line x1="4" y1="20" x2="21" y2="3" /><polyline points="21 16 21 21 16 21" /><line x1="15" y1="15" x2="21" y2="21" /><line x1="4" y1="4" x2="9" y2="9" />
                    </svg>
                    {submitting ? 'Traduction...' : 'Traduire automatiquement'}
                  </button>
                </div>
                <p style={{ fontSize: '0.75rem', color: '#888', margin: 0 }}>
                  Remplit automatiquement le titre, la description et la catégorie en Anglais et Arabe.
                </p>
              </div>

              <div className="act-admin-form-actions">
                <button type="button" onClick={closeForm} className="btn-cancel-act">Annuler</button>
                <button type="submit" className="btn-submit-partner" style={{ flex: 2 }} disabled={submitting}>
                  {submitting ? 'Enregistrement...' : editing ? 'Enregistrer les modifications' : 'Ajouter l\'activité'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ── Grille de cartes ── */}
        {viewMode === 'grid' && (
          <div className="act-admin-grid">
            {loading ? (
              <p className="loading-text">Chargement...</p>
            ) : activites.length === 0 ? (
              <p className="empty-table" style={{ gridColumn: '1/-1' }}>Aucune activité pour le moment.</p>
            ) : filteredActivites.length === 0 ? (
              <div className="admin-search-empty" style={{ gridColumn: '1/-1' }}>
                <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: '12px', color: '#888' }}>
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <p style={{ color: '#666', fontWeight: 600, margin: '0 0 12px 0' }}>Aucune activité ne correspond à votre recherche.</p>
                <button className="btn-add-partner" style={{ background: '#facd16', color: '#1a1a1a', border: 'none', padding: '10px 18px', borderRadius: '10px', fontWeight: '700', cursor: 'pointer' }} onClick={() => { setSearchQuery(''); setActiveFilter('ALL'); }}>
                  Réinitialiser la recherche
                </button>
              </div>
            ) : filteredActivites.map(a => {
              const catStyle = { bg: 'rgba(245,197,10,0.15)', color: '#b45309' };
              const firstPhoto = a.photos?.[0]?.image || null;
              return (
                <div key={a.id} className="act-admin-card">
                  <div className="act-admin-card-img">
                    {firstPhoto
                      ? <img src={firstPhoto} alt={a.titre} />
                      : <div className="act-admin-card-img-placeholder">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <polyline points="21 15 16 10 5 21" />
                        </svg>
                      </div>
                    }
                    <span className={`act-admin-badge-status ${a.is_active ? 'visible' : 'masquee'}`}>
                      {a.is_active ? 'Visible' : 'Masquée'}
                    </span>
                    <span className="act-admin-badge-photos">
                      <IconPhoto /> {a.photos?.length || 0}
                    </span>
                  </div>
                  <div className="act-admin-card-body">
                    <span className="act-admin-cat-badge" style={{ background: catStyle.bg, color: catStyle.color }}>
                      {a.categorie ? a.categorie.charAt(0).toUpperCase() + a.categorie.slice(1).toLowerCase() : 'Non catégorisé'}
                    </span>
                    <h4 className="act-admin-card-title">{a.titre}</h4>
                    <p className="act-admin-card-desc">{a.description}</p>
                    <span className="act-admin-card-date">{new Date(a.created_at).toLocaleDateString('fr-FR')}</span>
                  </div>
                  {canManage && (
                    <div className="act-admin-card-actions">
                      <button className="act-admin-btn" onClick={() => openPhotoModal(a)}>
                        <IconPhoto /> Photos
                      </button>
                      <button className="act-admin-btn edit" onClick={() => openEdit(a)}>
                        <IconEdit /> Modifier
                      </button>
                      <button className="act-admin-btn delete" onClick={() => handleDelete(a.id)}>
                        <IconTrash />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Vue liste ── */}
        {viewMode === 'list' && (
          <div className="admin-table-container">
            {loading ? <p className="loading-text">Chargement...</p> : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Photo</th>
                    <th>Titre</th>
                    <th>Catégorie</th>
                    <th>Statut</th>
                    <th>Photos</th>
                    <th>Date</th>
                    {canManage && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {activites.length === 0 ? (
                    <tr><td colSpan={canManage ? 7 : 6} className="empty-table">Aucune activité pour le moment.</td></tr>
                  ) : filteredActivites.length === 0 ? (
                    <tr>
                      <td colSpan={canManage ? 7 : 6} className="empty-table">
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <p style={{ color: '#666', fontWeight: 600, margin: '0 0 10px 0' }}>Aucune activité ne correspond à votre recherche.</p>
                          <button className="admin-reset-link" style={{ background: 'none', border: 'none', color: '#b45309', fontWeight: '700', textDecoration: 'underline', cursor: 'pointer' }} onClick={() => { setSearchQuery(''); setActiveFilter('ALL'); }}>
                            Réinitialiser
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : filteredActivites.map(a => {
                    const firstPhoto = a.photos?.[0]?.image || null;
                    return (
                      <tr key={a.id}>
                        <td>{firstPhoto ? <img src={firstPhoto} alt={a.titre} className="table-logo-preview" /> : '—'}</td>
                        <td><strong>{a.titre}</strong></td>
                        <td>{a.categorie || '—'}</td>
                        <td><span className={`act-admin-badge-status ${a.is_active ? 'visible' : 'masquee'}`}>{a.is_active ? 'Visible' : 'Masquée'}</span></td>
                        <td>{a.photos?.length || 0}</td>
                        <td>{new Date(a.created_at).toLocaleDateString('fr-FR')}</td>
                        {canManage && (
                          <td>
                            <div className="action-btns">
                              <button className="icon-btn" title="Photos" onClick={() => openPhotoModal(a)}><IconPhoto /></button>
                              <button className="icon-btn" title="Modifier" onClick={() => openEdit(a)}><IconEdit /></button>
                              <button className="icon-btn" title="Supprimer" style={{ color: '#e74c3c' }} onClick={() => handleDelete(a.id)}><IconTrash /></button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </section>
    </>
  );
}

export default AdminActivitesContent;
